import { useQuery } from '@tanstack/react-query';
import {
  ProfilesApi,
  ProfileVisibility,
  type TeamMemberWithUserDto,
  TeamsApi,
} from '@up2stay/up2promo-api-client';

import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import type { TeamMember } from '@/core/types/profile-modules';

function formatUserName(member: TeamMemberWithUserDto): string {
  const user = member.user;
  if (!user) {
    return 'Unbekannt';
  }
  const full =
    `${(user.firstName ?? '').trim()} ${(user.lastName ?? '').trim()}`.trim();
  return full || user.email?.trim() || 'Unbekannt';
}

function toDisplayMember(
  member: TeamMemberWithUserDto,
  index: number,
  enriched?: {
    role?: string | null;
    profileUrl?: string | null;
  },
): TeamMember {
  const user = member.user;
  const photoPath = user?.photo?.path;
  return {
    id: member.userId,
    userId: member.userId,
    username: user?.username,
    name: formatUserName(member),
    role: enriched?.role ?? undefined,
    profileUrl: enriched?.profileUrl ?? undefined,
    avatar: photoPath
      ? {
          id: (user?.photo as { id?: string } | undefined)?.id ?? member.userId,
          path: photoPath,
        }
      : undefined,
    order: index,
    visible: true,
  };
}

async function resolveTeamId(
  workspaceId: string,
  templateTeamIds?: string[],
): Promise<string | null> {
  const fromTemplate = templateTeamIds?.find((id) => id.trim());
  if (fromTemplate) {
    return fromTemplate;
  }

  const teamsRes = await TeamsApi.findAll({
    path: { workspaceId },
    query: { page: 1, limit: 5, onlyMine: true },
  });
  return teamsRes.data?.data?.[0]?.id ?? null;
}

export function useTeamMembersDisplay(
  workspaceId: string | null | undefined,
  options?: {
    templateTeamIds?: string[];
    enabled?: boolean;
    /** Optional: used to build profile links like `/${workspaceUsername}/${username}`. */
    workspaceUsername?: string | null;
  },
) {
  const enabled = Boolean(workspaceId) && (options?.enabled ?? true);
  const templateTeamIds = options?.templateTeamIds;
  const workspaceUsername = options?.workspaceUsername ?? null;

  const query = useQuery({
    queryKey: [
      'team-members-display',
      workspaceId,
      templateTeamIds?.join(',') ?? '',
      workspaceUsername ?? '',
    ],
    enabled,
    queryFn: async () => {
      const teamId = await resolveTeamId(
        workspaceId as string,
        templateTeamIds,
      );
      if (!teamId) {
        return { teamId: null, teamName: null, members: [] as TeamMember[] };
      }

      const [teamRes, membersRes] = await Promise.all([
        TeamsApi.findOne({
          path: { workspaceId: workspaceId as string, teamId },
        }),
        TeamsApi.listMembers({
          path: { workspaceId: workspaceId as string, teamId },
        }),
      ]);

      const apiMembers = membersRes.data ?? [];

      // Enrich members with role + link (best-effort) to match public profile cards.
      const enrichPairs = await Promise.all(
        apiMembers.map(async (member) => {
          try {
            const res = await ProfilesApi.findByUserAndWorkspace({
              path: {
                userId: member.userId,
                workspaceId: workspaceId as string,
              },
            });
            const p = res.data as {
              jobTitle?: string | null;
              visibility?: ProfileVisibility | string;
            };
            const role =
              p?.jobTitle != null && String(p.jobTitle).trim() !== ''
                ? String(p.jobTitle)
                : null;
            const canLink =
              p?.visibility != null &&
              String(p.visibility) !== ProfileVisibility.PRIVATE &&
              workspaceUsername &&
              member.user?.username;
            const profileUrl = canLink
              ? `/${workspaceUsername}/${member.user?.username}`
              : null;
            return [member.userId, { role, profileUrl }] as const;
          } catch {
            return [member.userId, { role: null, profileUrl: null }] as const;
          }
        }),
      );
      const enrichedByUserId = new Map(enrichPairs);

      return {
        teamId,
        teamName: teamRes.data?.name ?? null,
        members: apiMembers.map((member, index) =>
          toDisplayMember(member, index, enrichedByUserId.get(member.userId)),
        ),
      };
    },
    staleTime: 30_000,
  });

  return {
    teamId: query.data?.teamId ?? null,
    teamName: query.data?.teamName ?? null,
    members: query.data?.members ?? [],
    isLoading: enabled && query.isLoading,
    isError: query.isError,
  };
}

export function getTeamMemberAvatarUrl(member: TeamMember): string | undefined {
  const path = member.avatar?.path;
  return path ? getAbsoluteFileUrl(path) : undefined;
}
